import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import type { DragEvent } from 'react';

import { Button } from '../ui/Button';

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = new Set([
  'text/csv',
  'application/vnd.ms-excel',
  'application/octet-stream',
]);

interface ImportDropzoneProps {
  onFileAccepted: (file: File) => void;
  disabled?: boolean;
  brokerLabel?: string;
}

function validateFile(file: File): string | null {
  const extensionOk = file.name.toLowerCase().endsWith('.csv');
  if (!ACCEPTED_TYPES.has(file.type) && !extensionOk) {
    return 'Upload a CSV file.';
  }
  if (file.size > MAX_BYTES) {
    return 'CSV imports must be 10MB or smaller.';
  }
  return null;
}

export function ImportDropzone({
  brokerLabel = 'broker',
  disabled = false,
  onFileAccepted,
}: ImportDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  function accept(file: File | undefined) {
    if (!file) return;
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setError(null);
    onFileAccepted(file);
    if (inputRef.current) inputRef.current.value = '';
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
    accept(event.dataTransfer.files[0]);
  }

  return (
    <div className="space-y-3">
      <div
        className={`flex min-h-48 flex-col items-center justify-center rounded border border-dashed p-6 text-center transition-colors ${
          isDragging ? 'border-primary bg-primary/5' : 'border-border bg-bg'
        }`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <Upload className="mb-3 h-8 w-8 text-primary" aria-hidden="true" />
        <p className="text-sm font-medium">Drop {brokerLabel} CSV here</p>
        <p className="mt-1 text-sm text-text-muted">Statements, activity, holdings, or trades.</p>
        <input
          ref={inputRef}
          className="sr-only"
          type="file"
          accept=".csv,text/csv"
          aria-label={`Select ${brokerLabel} CSV`}
          disabled={disabled}
          onChange={(event) => accept(event.target.files?.[0])}
        />
        <Button
          type="button"
          variant="secondary"
          className="mt-4 gap-2"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          Select CSV
        </Button>
      </div>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}
